import { useEffect, useState } from 'react';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Switch } from '@/components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Server, Cloud, Database, Puzzle, AlertCircle } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { SearchSelect } from '@/components/ui/search-select';
import { V2SubscriptionForm } from '@/components/schools/V2SubscriptionForm';
import { V1DeploymentStatus } from '@/components/schools/V1DeploymentStatus';
import type { WizardFormData, DeploymentFormData } from '../types';
import type { SchoolOptions, CreateSchoolResponse } from '@/types/school';

interface DeploymentStepProps {
  formData: WizardFormData;
  onChange: (data: Partial<WizardFormData>) => void;
  options: SchoolOptions | null;
  isDeploying?: boolean;
  creationResult?: CreateSchoolResponse | null;
  creationProgress?: {
    database: boolean | null;
    folder: boolean | null;
    informations: boolean | null;
  };
  /** Mode passage V2 : seule la carte V2 est proposée, toujours active */
  v2Only?: boolean;
}

export function DeploymentStep({
  formData,
  onChange,
  options,
  isDeploying = false,
  creationResult,
  creationProgress,
  v2Only = false,
}: DeploymentStepProps) {
  const physicalNetwork =
    (options?.networks || []).find(
      (n) => n.type === 'physical' && n.id === formData.physicalNetworkId
    ) || null;

  const handleV1Change = (data: Partial<DeploymentFormData>) => {
    onChange({
      deployV1: { ...formData.deployV1, ...data },
    });
  };

  const handleV2Change = (data: Partial<DeploymentFormData>) => {
    onChange({
      deployV2: { ...formData.deployV2, ...data },
    });
  };

  const handleModuleToggle = (moduleId: number) => {
    const currentModules = formData.deployV1.moduleIds || [];
    const newModules = currentModules.includes(moduleId)
      ? currentModules.filter((id) => id !== moduleId)
      : [...currentModules, moduleId];
    handleV1Change({ moduleIds: newModules });
  };

  const noDeploymentSelected = !v2Only && !formData.deployV1.enabled && !formData.deployV2.enabled;

  // Show V1 deployment progress when deploying (V2 is handled by V2DeployDialog)
  if ((isDeploying || creationProgress) && formData.deployV1.enabled && creationResult) {
    return (
      <div className="space-y-6">
        <div className="text-center mb-8">
          <h2 className="text-xl font-semibold text-foreground">
            Déploiement V1 en cours...
          </h2>
          <p className="text-sm text-muted-foreground mt-1">
            Veuillez patienter pendant le déploiement de l'école <strong>{formData.name}</strong>
          </p>
        </div>

        <div className="max-w-lg mx-auto">
          <Card className="border-blue-200">
            <CardHeader className="pb-3">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-blue-100">
                  <Server className="h-5 w-5 text-blue-600" />
                </div>
                <CardTitle className="text-lg">Version 1</CardTitle>
              </div>
            </CardHeader>
            <CardContent>
              <V1DeploymentStatus
                schoolCode={creationResult.code}
                autoRefresh={true}
                refreshInterval={2000}
              />
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Show deployment selection form
  return (
    <div className="space-y-6 max-w-4xl mx-auto">
      <div className="text-center mb-8">
        <h2 className="text-xl font-semibold text-foreground">
          {v2Only ? 'Déploiement V2' : 'Déploiement'}
        </h2>
        <p className="text-sm text-muted-foreground mt-1">
          {v2Only ? (
            <>
              Les informations de <strong>{formData.name}</strong> sont enregistrées.
              Choisissez l'abonnement puis lancez le déploiement V2.
            </>
          ) : (
            <>
              L'école <strong>{formData.name}</strong> a été créée. Choisissez sur quelle(s) version(s) la déployer.
            </>
          )}
        </p>
      </div>

      {noDeploymentSelected && (
        <Alert>
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            Vous pouvez passer cette etape et deployer l'ecole plus tard depuis sa fiche.
          </AlertDescription>
        </Alert>
      )}

      <div className={v2Only ? 'max-w-lg mx-auto' : 'grid grid-cols-1 lg:grid-cols-2 gap-6'}>
        {/* V1 Deployment Card */}
        {!v2Only && (
        <Card className={`transition-all ${formData.deployV1.enabled ? 'border-blue-500 shadow-md' : 'opacity-75'}`}>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className={`p-2 rounded-lg ${formData.deployV1.enabled ? 'bg-blue-100' : 'bg-muted'}`}>
                  <Server className={`h-5 w-5 ${formData.deployV1.enabled ? 'text-blue-600' : 'text-muted-foreground'}`} />
                </div>
                <CardTitle className="text-lg">Version 1</CardTitle>
              </div>
              <Switch
                checked={formData.deployV1.enabled}
                onCheckedChange={(checked) => handleV1Change({ enabled: checked })}
              />
            </div>
          </CardHeader>

          {formData.deployV1.enabled && (
            <CardContent className="space-y-4 pt-0">
              {/* Status */}
              <div className="space-y-2">
                <Label>Statut initial</Label>
                <Select
                  value={formData.deployV1.status}
                  onValueChange={(value) => handleV1Change({ status: value as DeploymentFormData['status'] })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Selectionner un statut" />
                  </SelectTrigger>
                  <SelectContent>
                    {options?.statuses.map((status) => (
                      <SelectItem key={status.value} value={status.value}>
                        {status.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Source School */}
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <Database className="h-4 w-4" />
                  École source
                </Label>
                <SearchSelect
                  options={options?.sourceSchools.map((school) => ({
                    value: school.id.toString(),
                    label: school.isDefault ? school.name : `${school.name} (${school.code})`,
                    searchText: `${school.name} ${school.code}`,
                  })) || []}
                  value={formData.deployV1.schoolSourceId?.toString() || (options?.sourceSchools.find(s => s.isDefault)?.id.toString() ?? '')}
                  onValueChange={(value) =>
                    handleV1Change({ schoolSourceId: parseInt(value) })
                  }
                  placeholder="Sélectionner une école source"
                />
              </div>

              {/* Modules */}
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <Puzzle className="h-4 w-4" />
                  Modules
                </Label>
                <div className="grid grid-cols-2 gap-2 max-h-48 overflow-y-auto border rounded-md p-2">
                  {options?.modules.filter(m => !m.isV2).map((module) => (
                    <div key={module.id} className="flex items-center space-x-2">
                      <Checkbox
                        id={`v1-module-${module.id}`}
                        checked={formData.deployV1.moduleIds.includes(module.id)}
                        onCheckedChange={() => handleModuleToggle(module.id)}
                      />
                      <Label
                        htmlFor={`v1-module-${module.id}`}
                        className="text-sm font-normal cursor-pointer"
                      >
                        {module.name}
                      </Label>
                    </div>
                  ))}
                </div>
              </div>
            </CardContent>
          )}
        </Card>
        )}

        {/* V2 Deployment Card */}
        <Card className={`transition-all ${formData.deployV2.enabled ? 'border-green-500 shadow-md' : 'opacity-75'}`}>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className={`p-2 rounded-lg ${formData.deployV2.enabled ? 'bg-green-100' : 'bg-muted'}`}>
                  <Cloud className={`h-5 w-5 ${formData.deployV2.enabled ? 'text-green-600' : 'text-muted-foreground'}`} />
                </div>
                <CardTitle className="text-lg">Version 2</CardTitle>
              </div>
              {!v2Only && (
                <Switch
                  checked={formData.deployV2.enabled}
                  onCheckedChange={(checked) => handleV2Change({ enabled: checked })}
                />
              )}
            </div>
          </CardHeader>

          {formData.deployV2.enabled && (
            <CardContent className="pt-0">
              <V2SubscriptionForm
                options={options}
                tenantGroups={[]}
                defaultTenantGroupId=""
                value={{
                  offerId: formData.deployV2.offerId,
                  optionIds: formData.deployV2.optionIds,
                  moduleIds: formData.deployV2.moduleIds,
                }}
                onChange={(data) => {
                  handleV2Change({
                    offerId: data.offerId,
                    optionIds: data.optionIds,
                    moduleIds: data.moduleIds,
                  });
                }}
                showTenantGroup={false}
              />
              {physicalNetwork ? (
                // École créée dans un réseau physique : la cible n'est plus une
                // question, le tenant naît dans le groupe du réseau.
                <p className="text-xs text-muted-foreground mt-3">
                  Le tenant sera créé dans le tenant group du réseau{' '}
                  <span className="font-medium text-foreground">{physicalNetwork.name}</span>, avec sa
                  marque blanche. Connexion via{' '}
                  <span className="font-medium text-foreground">
                    {formData.v2Host || physicalNetwork.v2Host}
                  </span>
                  .
                </p>
              ) : (
                <p className="text-xs text-muted-foreground mt-3">
                  Le tenant group cible (réseau physique ou groupe École Futée par défaut) se
                  choisit au moment du déploiement.
                </p>
              )}
            </CardContent>
          )}
        </Card>
      </div>

      {/* Summary of selection */}
      <div className="text-center text-sm text-muted-foreground mt-6">
        {v2Only ? (
          <p>
            Cliquez sur « Déployer » pour créer le tenant V2 de l'école.
          </p>
        ) : noDeploymentSelected ? (
          <p>Cliquez sur "Voir la fiche" pour terminer sans deploiement.</p>
        ) : (
          <p>
            L'école sera déployée sur :{' '}
            <span className="font-medium text-foreground">
              {[
                formData.deployV1.enabled && 'V1',
                formData.deployV2.enabled && 'V2',
              ]
                .filter(Boolean)
                .join(' et ')}
            </span>
          </p>
        )}
      </div>
    </div>
  );
}
